// Socket para comunicación directa backend - frontend
import PropTypes from "prop-types";
import React, { createContext, useContext, useEffect, useState, useRef, useCallback } from "react";
import { io } from "socket.io-client";
import { useAuth } from "./AuthContext";
import { useNotification } from "./NotificationContext";
import { useLocation } from "react-router-dom";

const SocketContext = createContext(null);

export const useSocket = () => {
  return useContext(SocketContext);
};

export const SocketProvider = ({ children }) => {
  const [socket, setSocket] = useState(null);
  const { user, token, logout, updateToken } = useAuth();
  const { showNotification } = useNotification();
  const location = useLocation();
  
  const socketRef = useRef(null);
  const isConnectingRef = useRef(false);
  const tokenVerifyIntervalRef = useRef(null);
  const userRef = useRef(user);
  const tokenRef = useRef(token);
  const mountedRef = useRef(true);
  const logoutRef = useRef(logout);
  const updateTokenRef = useRef(updateToken);
  const showNotificationRef = useRef(showNotification);
  const locationRef = useRef(location);
  
  userRef.current = user;
  tokenRef.current = token;
  logoutRef.current = logout;
  updateTokenRef.current = updateToken;
  showNotificationRef.current = showNotification;
  locationRef.current = location;

  const createSocketConnection = useCallback((connectionType = 'general') => {
    if (!userRef.current || !tokenRef.current) {
      showNotificationRef.current({
        message: "No se puede conectar al sistema: credenciales no disponibles",
        severity: "error",
        position: { vertical: 'bottom', horizontal: 'left' },
        autoHideDuration: 4000,
      });
      
      return null;
    }

    if (isConnectingRef.current) {
      return socketRef.current;
    }

    if (socketRef.current?.connected) {
      return socketRef.current;
    }

    isConnectingRef.current = true;

    const socketUrl = process.env.REACT_APP_ENV === 'production'
      ? process.env.REACT_APP_API_URL_IO_PRODUCTION
      : process.env.REACT_APP_API_URL_IO;
    
    const baseConfig = {
      transports: ["websocket", "polling"],
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 20000,
      forceNew: false,
      upgrade: true,
      rememberUpgrade: true,
      autoConnect: true,
      auth: {
        token: tokenRef.current,
      },
    };

    let queryConfig = {
      userId: userRef.current.id,
      userEmail: userRef.current.email,
      userName: `${userRef.current.first_name?.trim() || ''} ${userRef.current.first_last_name?.trim() || ''} ${
        userRef.current.second_last_name?.trim() || ''
      }`.trim() || 'Usuario',
      userRole: userRef.current.role_name,
      documentNumber: userRef.current.document_number,
      clientVersion: '1.0.0',
      connectionTime: Date.now(),
    };

    switch (connectionType) {
      case 'whatsapp':
        queryConfig.isWhatsAppClient = true;
        queryConfig.service = 'whatsapp';
        break;
      case 'qr-admin':
        queryConfig.clientType = 'qr-admin';
        queryConfig.service = 'qr-admin';
        break;
      case 'vehicle-tracking':
        queryConfig.service = 'vehicle-tracking';
        break;
      default:
        queryConfig.service = 'general';
        break;
    }

    const newSocket = io(socketUrl, {
      ...baseConfig,
      query: queryConfig,
    });

    const setupSocketEvents = () => {
      newSocket.on('connect', () => {
        if (!mountedRef.current) return;
        
        isConnectingRef.current = false;
      });

      newSocket.on('backend:connected', (data) => {
        if (!mountedRef.current) return;
        
        const currentToken = tokenRef.current;
        
        if (currentToken) {
          newSocket.emit('verify_token', { token: currentToken }, (response) => {
            if (!mountedRef.current) return;
            
            if (!response.success) {
              showNotificationRef.current({
                message: response.message || "Su sesión ha expirado",
                severity: "error",
                position: { vertical: 'bottom', horizontal: 'left' },
                autoHideDuration: 4000,
              });
              logoutRef.current();
              return;
            }
            
            if (response.tokenRenewed && response.newToken) {
              updateTokenRef.current(response.newToken);
              tokenRef.current = response.newToken;
              // Actualizar auth del socket para reconexiones
              newSocket.auth = { ...newSocket.auth, token: response.newToken };
              
              showNotificationRef.current({
                message: "Sesion renovada automaticamente",
                severity: "info",
                position: { vertical: 'bottom', horizontal: 'left' },
                autoHideDuration: 2000,
              });
            } else {
              showNotificationRef.current({
                message: userRef.current?.first_name 
                  ? `${userRef.current.first_name.trim()} conectado al sistema`
                  : "Conexion establecida exitosamente",
                severity: "success",
                position: { vertical: 'bottom', horizontal: 'left' },
                autoHideDuration: 3000,
              });
            }
            
            socketRef.current = newSocket;
            setSocket(newSocket);

            // Iniciar verificacion periodica del token (cada 30 min)
            if (tokenVerifyIntervalRef.current) {
              clearInterval(tokenVerifyIntervalRef.current);
            }
            tokenVerifyIntervalRef.current = setInterval(() => {
              if (socketRef.current?.connected && tokenRef.current) {
                socketRef.current.emit('verify_token', { token: tokenRef.current }, (verifyResponse) => {
                  if (!mountedRef.current) return;
                  if (!verifyResponse.success) {
                    showNotificationRef.current({
                      message: verifyResponse.message || "Su sesion ha expirado",
                      severity: "error",
                      position: { vertical: 'bottom', horizontal: 'left' },
                      autoHideDuration: 4000,
                    });
                    logoutRef.current();
                    return;
                  }
                  if (verifyResponse.tokenRenewed && verifyResponse.newToken) {
                    updateTokenRef.current(verifyResponse.newToken);
                    tokenRef.current = verifyResponse.newToken;
                    if (socketRef.current) {
                      socketRef.current.auth = { ...socketRef.current.auth, token: verifyResponse.newToken };
                    }
                  }
                });
              }
            }, 30 * 60 * 1000);
          });
        } else {
          showNotificationRef.current({
            message: "Sesión no válida",
            severity: "error",
            position: { vertical: 'bottom', horizontal: 'left' },
            autoHideDuration: 3000,
          });
          logoutRef.current();
        }
      });

      newSocket.on('connection_confirmed', (data) => {
        // Confirmación del backend recibida
      });

      newSocket.on("token_refresh", ({ token: newToken, renewedAt, expiresIn, reason }) => {
        if (!mountedRef.current) return;
        updateTokenRef.current(newToken);
        tokenRef.current = newToken;
        // Actualizar auth del socket para reconexiones
        newSocket.auth = { ...newSocket.auth, token: newToken };
        
        if (reason && reason !== 'periodic_check') {
          showNotificationRef.current({
            message: "Su sesion ha sido renovada automaticamente",
            severity: "info",
            position: { vertical: 'bottom', horizontal: 'left' },
            autoHideDuration: 2000,
          });
        }
      });

      newSocket.on("token_verified", (data) => {
        if (!mountedRef.current) return;
        if (data.tokenRenewed && data.newToken) {
          updateTokenRef.current(data.newToken);
          tokenRef.current = data.newToken;
          newSocket.auth = { ...newSocket.auth, token: data.newToken };
        }
      });

      newSocket.on("token_invalid", (data) => {
        showNotificationRef.current({
          message: data.message || "Su sesión ha expirado",
          severity: "error",
          position: { vertical: 'bottom', horizontal: 'left' },
          autoHideDuration: 4000,
        });
        logoutRef.current();
      });

      newSocket.on("token_expired", ({ message, expiredAt }) => {
        showNotificationRef.current({
          message: message || "Su sesión ha expirado. Por favor, inicie sesión nuevamente.",
          severity: "error",
          position: { vertical: 'bottom', horizontal: 'left' },
          autoHideDuration: 4000,
        });
        
        logoutRef.current();
      });

      newSocket.on("authentication_error", (error) => {
        showNotificationRef.current({
          message: "Sesión expirada. Por favor, inicie sesión nuevamente.",
          severity: "error",
          position: { vertical: 'bottom', horizontal: 'left' },
          autoHideDuration: 6000,
        });
        logoutRef.current();
      });

      newSocket.on("disconnect", (reason) => {
        if (!mountedRef.current) return;
        
        isConnectingRef.current = false;
        
        // Limpiar verificacion periodica
        if (tokenVerifyIntervalRef.current) {
          clearInterval(tokenVerifyIntervalRef.current);
          tokenVerifyIntervalRef.current = null;
        }
        
        if (socketRef.current === newSocket) {
          socketRef.current = null;
          setSocket(null);
        }

        if (reason === "io server disconnect") {
          showNotificationRef.current({
            message: "Servidor desconectó la sesión",
            severity: "warning",
            position: { vertical: 'bottom', horizontal: 'left' },
            autoHideDuration: 3000,
          });
        } else if (reason === "transport error") {
          showNotificationRef.current({
            message: "Error de conexión. Intentando reconectar...",
            severity: "warning",
            position: { vertical: 'bottom', horizontal: 'left' },
            autoHideDuration: 3000,
          });
        }
      });

      newSocket.on("connect_error", (error) => {
        isConnectingRef.current = false;

        if (
          error.message.includes("Autenticación inválida") ||
          error.message.includes("token") ||
          error.message.includes("Token expirado")
        ) {
          showNotificationRef.current({
            message: "Sesión expirada. Por favor, inicie sesión nuevamente.",
            severity: "error",
            position: { vertical: 'bottom', horizontal: 'left' },
            autoHideDuration: 4000,
          });
          logoutRef.current();
        }
      });

      newSocket.on('reconnect', (attemptNumber) => {
        isConnectingRef.current = false;
        
        showNotificationRef.current({
          message: "Reconectado al sistema exitosamente",
          severity: "success",
          position: { vertical: 'bottom', horizontal: 'left' },
          autoHideDuration: 2000,
        });
      });

      newSocket.on('reconnect_attempt', (attemptNumber) => {
        // Intentando reconectar
      });

      newSocket.on('reconnect_failed', () => {
        isConnectingRef.current = false;
        
        showNotificationRef.current({
          message: "No se pudo reconectar. Por favor, recargue la página.",
          severity: "error",
          position: { vertical: 'bottom', horizontal: 'left' },
          autoHideDuration: 6000,
        });
      });

      newSocket.on('reconnect_error', (error) => {
        // Error durante reconexión
      });
    };

    setupSocketEvents();
    return newSocket;
  }, []);

  useEffect(() => {
    mountedRef.current = true;

    if (user && token) {
      const connectionTimeout = setTimeout(() => {
        if (!mountedRef.current) return;
        
        const newSocket = createSocketConnection();
        
        if (!newSocket) {
          // No se pudo crear socket
        }
      }, 100);

      return () => {
        clearTimeout(connectionTimeout);
        mountedRef.current = false;
      };
    } else {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
        setSocket(null);
      }
      if (tokenVerifyIntervalRef.current) {
        clearInterval(tokenVerifyIntervalRef.current);
        tokenVerifyIntervalRef.current = null;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, token]);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
      isConnectingRef.current = false;
      
      if (tokenVerifyIntervalRef.current) {
        clearInterval(tokenVerifyIntervalRef.current);
        tokenVerifyIntervalRef.current = null;
      }
      
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, []);

  // Listener global de notificaciones de chat
  // ChatProvider solo existe en /message, asi que este listener cubre el resto de la app
  useEffect(() => {
    if (!socket || !user) return;

    const handleGlobalChatNotification = (data) => {
      if (!mountedRef.current) return;
      // Solo notificar si NO estamos en la pagina de chat
      if (locationRef.current.pathname === '/message') return;
      // No notificar mensajes propios
      if (data.sender?.id === userRef.current?.id) return;

      const senderName = data.sender
        ? `${data.sender.first_name} ${data.sender.first_last_name}`.trim()
        : 'Nuevo mensaje';
      showNotificationRef.current({
        message: `${senderName}: ${(data.content || '').substring(0, 60)}`,
        severity: 'info',
        position: { vertical: 'top', horizontal: 'left' },
        autoHideDuration: 5000,
      });
    };

    socket.on('chat:new-message', handleGlobalChatNotification);
    return () => {
      socket.off('chat:new-message', handleGlobalChatNotification);
    };
  }, [socket, user]);

  return (
    <SocketContext.Provider value={socket}>{children}</SocketContext.Provider>
  );
};

SocketProvider.propTypes = {
  children: PropTypes.node.isRequired,
};